// Forward Message Component
// Following existing patterns from ConversationList

import React, { useState, useCallback } from 'react';
import { ForwardMessageProps } from '../../../types/messaging';
import {
  ForwardContainer,
  ForwardHeader,
  ForwardOriginalMessage,
  ForwardContent,
  ForwardActions,
  ForwardButton,
  CancelButton,
  ConversationSelector,
  ConversationOption,
  ConversationAvatar,
  ConversationInfo,
  ConversationName,
  SearchInput
} from '../../../styles/Messaging/MessageActions.styles';

export const ForwardMessage: React.FC<ForwardMessageProps> = ({
  message,
  conversations,
  onForwardMessage,
  onCancel,
  disabled = false
}) => {
  const [selectedConversationId, setSelectedConversationId] = useState<string>('');
  const [forwardText, setForwardText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = conversations.filter(conv => 
    conv.participants.some(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleForward = useCallback(async () => {
    if (!selectedConversationId) return;
    if (disabled) return;

    try {
      await onForwardMessage(selectedConversationId, message.id, forwardText.trim());
      setSelectedConversationId('');
      setForwardText('');
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to forward message:', error);
    }
  }, [selectedConversationId, message.id, forwardText, onForwardMessage, disabled]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <ForwardContainer>
      <ForwardHeader>
        <span>Forward Message</span>
        <CancelButton onClick={onCancel}>✕</CancelButton>
      </ForwardHeader>
      
      <ForwardOriginalMessage>
        <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
          From {message.senderName} • {formatTime(message.createdAt)}
        </div>
        <div style={{ fontSize: '0.9rem' }}>
          {message.content}
        </div>
      </ForwardOriginalMessage>
      
      <ForwardContent>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
            Forward to:
          </label>
          <SearchInput
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          <ConversationSelector>
            {filteredConversations.map((conversation) => {
              const otherParticipant = conversation.participants.find(p => p.id !== 'current-user');
              const isSelected = selectedConversationId === conversation.id;
              
              return (
                <ConversationOption
                  key={conversation.id}
                  $isSelected={isSelected}
                  onClick={() => setSelectedConversationId(conversation.id)}
                >
                  <ConversationAvatar>
                    {otherParticipant?.avatar ? (
                      <img 
                        src={otherParticipant.avatar} 
                        alt={otherParticipant.name}
                        style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                      />
                    ) : (
                      getInitials(otherParticipant?.name || 'U')
                    )}
                  </ConversationAvatar>
                  
                  <ConversationInfo>
                    <ConversationName>{otherParticipant?.name || 'Unknown User'}</ConversationName>
                  </ConversationInfo>
                </ConversationOption>
              );
            })}
          </ConversationSelector>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
            Add a message (optional):
          </label>
          <textarea
            value={forwardText}
            onChange={(e) => setForwardText(e.target.value)}
            placeholder="Add a comment..."
            disabled={disabled}
            rows={3}
            style={{
              width: '100%',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '0.75rem',
              fontSize: '0.9rem',
              resize: 'none',
              outline: 'none',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </ForwardContent>
      
      <ForwardActions>
        <CancelButton onClick={onCancel}>
          Cancel
        </CancelButton>
        <ForwardButton
          onClick={handleForward}
          disabled={!selectedConversationId || disabled}
        >
          Forward
        </ForwardButton>
      </ForwardActions>
    </ForwardContainer>
  );
};
